import type { Post } from "@/sanity.types"
import CalendarL from "images/Icons/CalendarL.inline.svg"
import TagL from "images/Icons/TagL.inline.svg"
import UserL from "images/Icons/UserL.inline.svg"
import { DisplayDate } from "library/DisplayDate"
import UniversalImage from "library/UniversalImage"
import UniversalLink from "library/link"
import { css, f, keyframes, styled } from "library/styled"
import colors from "styles/colors"
import textStyles, { clampText } from "styles/text"

export function SmallCard({
	post,
	className,
}: {
	post: Post | null | undefined
	className?: string
}) {
	if (!post) return null
	const {
		slug,
		author,
		publishOverride,
		mainImage,
		categories,
		title,
		preview,
		_createdAt,
	} = post

	const published = publishOverride ?? _createdAt

	const publishedDate = (
		<DisplayDate
			date={published}
			options={{
				year: "numeric",
				month: "long",
				day: "numeric",
			}}
		/>
	)

	const link = post?.externalUrl ?? `/blog/post/${slug?.current}`

	if (!link) return null

	return (
		<Wrapper href={link} className={className}>
			<PreventInitialTransition />
			<TopRow>
				<Author>
					<Icon>
						<UserL />
					</Icon>
					{author}
				</Author>
				<PublishDate>
					<Icon>
						<CalendarL />
					</Icon>
					{publishedDate ?? "Today"}
				</PublishDate>
			</TopRow>
			<ImageWrapper>
				<BlogImage src={mainImage} width={405} height={280} loading="default" />
			</ImageWrapper>
			<Tag>
				<Icon>
					<TagL />
				</Icon>
				{categories?.[0]}
			</Tag>
			<Line />
			<TitleContent>
				<Title>{title}</Title>
				<Description>{preview}</Description>
			</TitleContent>
		</Wrapper>
	)
}

const TopRow = styled("div", {
	...f.responsive(css`
		display: flex;
		justify-content: space-between;
		align-items: center;
		height: 16px;
	`),
})

const Author = styled("div", {
	...f.responsive(css`
		display: flex;
		align-items: center;
		gap: 6px;
		${textStyles.titleS};
		color: ${colors.green400};
		white-space: nowrap;
	`),

	...f.mobile(css`
		${textStyles.titleXS};
	`),
})

const PublishDate = styled("div", {
	...f.responsive(css`
		display: flex;
		align-items: center;
		gap: 6px;
		${textStyles.titleS};
		color: ${colors.green400};
	`),

	...f.mobile(css`
		${textStyles.titleXS};
	`),
})

const PreventInitialTransition = keyframes`
	0%,
	100% {
		transition: none;
	}
`

const BlogImage = styled(UniversalImage, {
	...f.responsive(css`
		width: 100%;
		height: 100%;

		img: {
			transform-origin: center;
			transform: scale(1);
			transition: transform 0.2s ease-in-out;
			animation: PreventInitialTransition 0.1s;
		}
	`),

	...f.tablet(css`
		width: 432px;
		height: 280px;
	`),

	...f.mobile(css`
		width: 311px;
		height: 216px;
	`),
})

const Wrapper = styled(UniversalLink, {
	...f.responsive(css`
		display: flex;
		flex-direction: column;
		flex-shrink: 0;
		left: 0;
		z-index: 8;
		border: 1px solid ${colors.green100};
		background-color: ${colors.offWhite};
		transition: 0.2s ease-in-out;
		cursor: pointer;
		height: 580px;
		width: 437px;
		padding: 16px;
		gap: 24px;
		border-radius: 16px;

		:hover {
			background-color: ${colors.js.green100}50;

			img {
				transition: transform 0.2s ease-in-out;
				transform: scale(1.07);
			}
		}

		:not(:hover) {
			img {
				transition: transform 0.2s ease-in-out;
				transform: scale(1);
			}
		}
	`),

	...f.tablet(css`
		height: 580px;
		width: 464px;
	`),

	...f.mobile(css`
		width: 327px;
		height: auto;
		gap: 16px;
	`),
})

const Line = styled("div", {
	...f.responsive(css`
		width: 100%;
		height: 1px;
		background-color: ${colors.green100};
	`),

	...f.mobile(css`
		margin: 8px 0;
	`),
})

const TitleContent = styled("div", {
	...f.responsive(css`
		display: flex;
		flex-direction: column;
		gap: 16px;
	`),
})

const Title = styled("h2", {
	...f.responsive(css`
		${textStyles.bodyL};
		color: ${colors.green500};
		${clampText(2)};
		padding: 5px 0;
		margin: -5px 0;
	`),

	...f.mobile(css`
		${clampText(2)};
	`),
})

const Description = styled("p", {
	...f.responsive(css`
		${textStyles.bodyXS};
		color: ${colors.green400};
		${clampText(3)};
		padding: 5px 0;
		margin: -5px 0;
	`),

	...f.mobile(css`
		${clampText(2)};
	`),
})

const Tag = styled("div", {
	...f.responsive(css`
		display: flex;
		align-items: center;
		${textStyles.titleS};
		color: ${colors.green400};
		gap: 6px;
	`),

	...f.mobile(css`
		${textStyles.titleXS};
	`),
})

const Icon = styled("div", {
	...f.responsive(css`
		width: 16px;
		height: 16px;
	`),

	svg: {
		width: "100%",
		height: "100%",
	},
})

const ImageWrapper = styled("div", {
	...f.responsive(css`
		position: relative;
		width: 405px;
		height: 280px;
		border-radius: 8px;
		object-fit: cover;
		overflow: clip;
	`),

	...f.tablet(css`
		width: 432px;
		height: 280px;
	`),

	...f.mobile(css`
		width: 295px;
		height: 216px;
	`),
})
